<template>
  <div class="container-fluid">
    <div class="row">
      <div
        role="main"
        class="col d-block"
      >
        <section class="d-flex justify-content-between align-items-center mt-2">
          <div class="section-title py-0">
            Shape Viewer
          </div>
        </section>
        <hr>
        <div class="row mb-2">
          <div class="col">
            <label class="mb-1">Show Location Shapes</label>
            <vue-typeahead-bootstrap
              placeholder="Start typing a location name..."
              v-model="typeahead.query"
              @hit="typeahead.selectedLocation = $event; typeahead.errors.location = null"
              :min-matching-chars="2"
              :data="typeahead.suggestions"
              :serializer="displayName"
              :disable-sort="true"
              :show-all-results="true"
              :disabled="form.loading"
            />
            <template v-if="!!location">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary mr-2 mt-2"
                @click="loadLocationShape"
                :disabled="childTypesLoaded.find( x => x.locationType === location.type)"
              >
                Load Own Shape
              </button>
              <button
                type="button"
                v-for="childType in location.childLocationTypes"
                :key="childType.type"
                class="btn btn-sm btn-outline-primary mr-2 mt-2"
                @click="() => loadLocationChildTypes(childType.type)"
                :disabled="childTypesLoaded.find( x => x.locationType === childType.type)"
              >
                Load {{ childType.total }}
                <template v-if="childType.total > 1">
                  {{ locationTypes.find(x => childType.type === x.key).namePlural }}
                </template>
                <template v-else>
                  {{ locationTypes.find(x => childType.type === x.key).name }}
                </template>
              </button>
            </template>
          </div>
          <!--div class="col-md-6">
            <label class="mb-1">Get External Shapes</label>
            <div class="d-flex flex-column">
              <form
                class="form-inline flex-fill"
                @submit.prevent="resolveShape"
              >
                <div class="form-group flex-fill">
                  <input
                    type="url"
                    class="form-control w-100"
                    placeholder="External URL"
                    v-model="resolveURL"
                    :disabled="resolvingShape"
                  >
                </div>
                <div class="form-group ml-2">
                  <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="resolvingShape"
                  >
                    Get
                  </button>
                </div>
              </form>
              <small
                v-if="resolveErrMsg"
                class="text-danger font-weight-bold"
              >
                {{ resolveErrMsg }}
              </small>
            </div>
          </div-->
        </div>
        <LocationMap
          :supplementary-layers="layers"
          @update="onMapUpdate"
        />
        <hr class="mt-3 mb-2">
        <p class="font-weight-bold mb-2">
          Current Map Features
        </p>
        <template v-if="features">
          <p class="mb-2">
            Showing {{ features.length }}
          </p>
          <ul
            class="list-group mb-2"
          >
            <li
              v-for="(feature, index) in features"
              :key="index"
              class="list-group-item"
            >
              <span>
                {{ index + 1 }}.
                <template v-if="feature.properties && feature.properties.LocationName">
                  <span class="font-weight-bold">{{ feature.properties.LocationName }}</span>
                </template>
                | <span class="text-muted">{{ feature.geometry.type }}</span>
              </span>
              <textarea
                class="form-control d-block mt-2"
                :value="JSON.stringify(feature.geometry)"
                rows="1"
                readonly
                disabled
              />
              <button
                type="button"
                class="btn btn-primary btn-sm d-inline-block mt-2"
                @click="() => copyToClipboard(JSON.stringify(feature.geometry))"
              >
                Copy Geo JSON
              </button>
              <button
                v-if="feature.properties && feature.properties.LocationName"
                type="button"
                class="btn btn-primary btn-sm d-inline-block mt-2"
                @click="() => copyToClipboard(feature.properties.LocationName)"
              >
                Copy Name
              </button>
            </li>
          </ul>
        </template>
        <template v-else>
          <p class="m-0">
            No features added yet
          </p>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import LocationMap from './LocationMap.vue';
import http from '../../http';
import notify from '../../notifications';
import geoAPI from '../../geoapi';
import locationTypes from '../../models/location-types';

export default {
    data() {
        return {
            location: null,
            form: {
                loading: false
            },
            typeahead: {
                suggestions: [],
                query: '',
                errors: {},
                selectedLocation: null
            },
            errors: {},
            childTypesLoaded: [],
            resolveURL: '',
            resolvingShape: false,
            resolveErrMsg: '',
            features: null
        };
    },
    computed: {
        locationTypes() {
            return locationTypes;
        },
        layers() {
            return this.childTypesLoaded.map( x => {
                return {
                    id: locationTypes.find( t => t.key === x.locationType ).namePlural,
                    data: x.data
                };
            } );
        }
    },
    methods: {
        async copyToClipboard( str ) {
            await window.navigator.clipboard.writeText( str );
            notify( {
                type: 'success',
                title: 'Copied!'
            } );
        },
        onMapUpdate( event ) {
            this.features = event.geojson.features;
        },
        async resolveShape() {
            this.resolveErrMsg = '';

            let url;

            try {
                url = new URL( this.resolveURL );
            } catch ( err ) {
                notify( {
                    type: 'error',
                    title: 'Failed parsing URL'
                } );
                return;
            }

            this.resolvingShape = true;

            const apiURL = new URL( process.env.GEO_RESOLVER_URL + '/getShape' );
            apiURL.searchParams.set( 'target', url.toString() );

            try {
                const response = await fetch( apiURL );

                if ( !response.ok ) {
                    let errTitle = 'Failed resolving external shape';
                    let errText = '';

                    const data = await response.json();

                    if ( data.errorMsg ) {
                        errTitle = data.errorMsg;
                    }

                    if ( data.error ) {
                        errText = data.error;
                        this.resolveErrMsg = data.error;
                    }

                    notify( {
                        type: 'error',
                        title: errTitle,
                        text: errText
                    } );

                    this.resolvingShape = false;

                    return;
                }
            } catch ( err ) {
                this.resolveErrMsg = err.toString();

                notify( {
                    type: 'error',
                    title: 'Failed resolving external shape',
                    text: err.toString()
                } );
            }

            this.resolvingShape = false;
        },
        async loadLocation() {
            if ( !this.typeahead.selectedLocation ) {
                return;
            }

            const response = await geoAPI.get( `/locations/db/${this.typeahead.selectedLocation.id}` );

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    title: 'Failed to load location by ID'
                } );
                return;
            }

            const location = await response.json();

            this.childTypesLoaded = [];
            this.location = location;
        },
        async loadLocationShape() {
            const params = new URLSearchParams();
            params.set( 'locationIds', this.location.id );

            const response = await geoAPI.get( `/${this.location.type}?${params}` );

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    title: 'Failed to load shape for selected location'
                } );
                return;
            }

            this.childTypesLoaded.push( {
                locationType: this.location.type,
                data: await response.json()
            } );
        },
        async loadLocationChildTypes( locationType ) {
            if ( !locationType ) {
                return;
            }

            const params = new URLSearchParams();
            params.set( 'parentId', this.location.id );
            params.set( 'parentType', this.location.type );
            params.set( 'locationType', locationType );

            const response = await geoAPI.get( `/locations/db?${params}` );

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    title: `Failed to load ${this.locationTypes[ locationType ].namePlural}`
                } );
                return;
            }

            const geojson = await response.json();

            if ( geojson.features ) {
                geojson.features = geojson.features.filter( f => f.geometry.coordinates.length > 0 );
            }

            this.childTypesLoaded.push( {
                locationType: locationType,
                data: geojson
            } );
        },
        parentName( location ) {
            let parentMunicipalities = location.relations.filter( r => r.objectType == 'municipality' );
            if ( parentMunicipalities.length > 0 ) {
                // case with multiple municipalities
                return `in ${parentMunicipalities.map( m => m.objectName ).join( '/' )} municipality`;
            }

            let parentProvinces = location.relations.filter( r => r.objectType == 'province' );
            if ( parentProvinces.length > 0 ) {
                return `in ${parentProvinces[ 0 ].objectName} province`;
            }

            return '';
        },
        displayName( location ) {
            let parentName = this.parentName( location );
            return `${location.name} (${location.id} ${location.type}) ${parentName}`;
        },
        async loadSuggestions( q ) {
            if ( !q || q.length < 2 ) {
                this.typeahead.suggestions = [];
                this.typeahead.selectedLocation = null;
                return;
            }

            const searchParams = new URLSearchParams();
            searchParams.set( 'limit', '10' );
            searchParams.set( isNaN( q ) ? 'freetext' : 'id', q );

            const response = await http.get( `locations?${searchParams.toString()}` );

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    text: 'Something went wrong',
                    title: 'Failed to load locations'
                } );
            } else {
                this.typeahead.suggestions = await response.json();
            }
        }
    },
    watch: {
        'typeahead.query'( q ) {
            this.loadSuggestions( q );
        },
        async 'typeahead.selectedLocation'( hit ) {
            if ( hit === null ) {
                return;
            }
            await this.loadLocation();
        }
    },
    components: {
        LocationMap
    }
};
</script>



// WEBPACK FOOTER //
// src/components/locations/ShapeViewer.vue